import React, { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react';
import { styled } from '@compiled/react';

import { Inline, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';

import { LiveDocSpotlightsTourManager } from '@confluence/onboarding-live-doc-changeboarding/entry-points/LiveDocSpotlightsTour';
import { Breadcrumbs } from '@confluence/breadcrumbs';
import { SaveIndicator } from '@confluence/save-indicator';
import {
	useIsDataClassificationEnabledForOrg,
	ClassificationEditModal,
	ClassificationModalContextProvider,
	EditorClassificationTagWrapper,
	useShouldRenderEditorActionBarClassificationPlaceholder,
} from '@confluence/data-classification';
import { EditorPrimaryToolbarComponents } from '@confluence/editor-primary-toolbar-components';
import { RequestToEditLoaderAfterPaint } from '@confluence/no-permission/entry-points/RequestToEditLoaderAfterPaint';
import { EditorPresenceAvatarGroupLoader } from '@confluence/editor-presence-avatar-group';
import { fg } from '@confluence/feature-gating';
import { ContentHeaderPresenceLoader } from '@confluence/team-presence/entry-points/ContentHeaderPresenceLoader';

import type { EditorActionBarProps } from './EditorActionBarProps';

// If any change of css happens that caused EditorActionBar to have a different height,
// Update EDITOR_ACTION_BAR_HEIGHT in EditorDisplayWrapper and EditorHelpCenterComponent and EditorDraftBannerStyles
const editorActionBarWrapperStyles = xcss({
	display: 'flex',
	position: 'relative',
	paddingLeft: 'space.300',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	justifyContent: 'space-between',
	borderBottomColor: 'color.border',
	borderBottomStyle: 'solid',
	borderBottomWidth: '1px',
	minHeight: '36px',
	maxHeight: '61px',
	// editor inline coment sidebar has a zIndex of 501, and editor toolbar has a zIndex of 510, set a slighly higher zIndex would fix overlap issue with popups from inline comments and invite to share popup.
	zIndex: 'flag',
	backgroundColor: 'elevation.surface',
});

const hiddenStyles = xcss({
	display: 'none',
});

const modernizedBreadcrumbsEditorActionBarWrapperStyles = xcss({
	paddingLeft: 'space.150',
	maxHeight: 'unset',
	alignItems: 'center',
	gap: 'space.025',
});

const overflowStyles = xcss({
	overflow: 'hidden',
});

const breadcrumbsAndClassificationTagWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'flex-start',
	flexDirection: 'column',
});

const noWrapBreadcrumbsStyles = xcss({
	alignItems: 'center',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	flex: '0 1 auto',
	overflow: 'hidden',
	justifyContent: 'start',
	minWidth: 'fit-content',
	gap: 'space.050',

	[media.above.xs]: {
		minWidth: '96px',
	},
});

const componentsWrapperStyles = xcss({
	display: 'flex',
	flexBasis: '100%',
	minWidth: 'fit-content',
	justifyContent: 'flex-end',
	alignItems: 'center',
});

const noWrapComponentsWrapperStyles = xcss({
	flexBasis: 'unset',
	marginLeft: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalSeparator = styled.div({
	borderLeft: `2px solid ${token('color.border')}`,
	display: 'inline-block',
	width: '1px',
	height: '16px',
	margin: `0 ${token('space.075')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span({
	marginRight: `${token('space.075')}`,
});

const EDITOR_ACTION_BAR_HIDE_BREADCRUMB_BREAK_POINT = 1000;

const EditorActionBar = ({
	spaceKey,
	editorActions,
	collabEditState,
	title,
	forceDisabled,
	userOffline,
	contentId,
	contentType,
	isContentCreationPanelOpen,
	isUnpublishedDraft,
	onClickPublish,
	onClickOpenContentCreationPanel,
	onPreviewClicked,
	saveDraft,
	collabLimitReached,
	showRevertDialog,
	retrieveEditorTitleAndContent,
	isPublishingContent,
	isSingleUserSession,
	navigateOnClose,
	triggerAnalyticsEvent,
	shouldRenderPreview,
	shouldShowRecentSpotlight,
	checkIfConditionPreventingPublish,
	flags,
	collabEditOption,
	hasEditPermission,
	editorDisabled,
	isTitleFocused,
	viewMode,
	contentClassificationMetadata,
	isLivePage,
	requestToEditContent,
	hasRequestedEditPermission,
	ellipsisEditorActions,
}: EditorActionBarProps) => {
	const { isEnabled: isDataClassificationEnabledForOrg } = useIsDataClassificationEnabledForOrg();

	const [hideBreadcrumb, setHideBreadcrumb] = useState(false);

	const classificationProps = {
		isDataClassificationEnabledForOrg,
		spaceKey,
		contentId,
		contentType,
		contentClassificationMetadata,
		hasEditPermission,
		isLivePage,
		userOffline,
	};

	const shouldRenderClassificationPlaceholder =
		useShouldRenderEditorActionBarClassificationPlaceholder(classificationProps);

	const resizeObserver = useMemo(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			return null;
		}

		return new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
			for (const entry of entries) {
				if (entry.contentRect.width) {
					if (entry.contentRect.width < EDITOR_ACTION_BAR_HIDE_BREADCRUMB_BREAK_POINT) {
						setHideBreadcrumb(true);
					}
					if (entry.contentRect.width > EDITOR_ACTION_BAR_HIDE_BREADCRUMB_BREAK_POINT + 20) {
						// Unhide breadcrumb with some buffer to avoid glitching
						setHideBreadcrumb(false);
					}
				}
			}
		});
	}, [setHideBreadcrumb]);

	// Hide breadcrumbs when content body width is smaller than the breakpoint
	useEffect(() => {
		// Content body does not include content tree
		const contentBody: HTMLElement | null = document.getElementById('content-body');

		if (!contentBody) {
			return;
		}

		resizeObserver?.observe(contentBody);

		return () => {
			resizeObserver?.unobserve(contentBody);
		};
	}, [resizeObserver]);

	const isCollabNetworkUnreachable =
		(editorDisabled || collabLimitReached) && !isTitleFocused && !userOffline;

	const hideSaveIndicator = viewMode || isCollabNetworkUnreachable;

	return (
		<Box
			xcss={[
				editorActionBarWrapperStyles,
				shouldRenderPreview && hiddenStyles,
				fg('confluence_modernized_breadcrumbs') &&
					modernizedBreadcrumbsEditorActionBarWrapperStyles,
			]}
			testId="editor-action-bar"
		>
			<LiveDocSpotlightsTourManager>
				<ClassificationModalContextProvider>
					<ClassificationEditModal
						contentId={contentId}
						spaceKey={spaceKey}
						contentClassificationMetadata={contentClassificationMetadata}
					/>
					<Box
						xcss={[
							breadcrumbsAndClassificationTagWrapperStyles,
							fg('confluence_modernized_breadcrumbs') && noWrapBreadcrumbsStyles,
						]}
					>
						<Inline
							xcss={[fg('confluence_modernized_breadcrumbs') && overflowStyles]}
							alignBlock="center"
						>
							{fg('confluence_modernized_breadcrumbs') ? (
								<Breadcrumbs
									contentId={contentId}
									spaceKey={spaceKey}
									contentTitle={title ?? undefined}
									testId="editor-breadcrumbs-container"
								/>
							) : (
								!hideBreadcrumb && (
									<Box data-testId="editor-breadcrumbs-container">
										<Breadcrumbs
											contentId={contentId}
											spaceKey={spaceKey}
											contentTitle={title ?? undefined}
										/>
									</Box>
								)
							)}
						</Inline>
						{!fg('confluence_modernized_breadcrumbs') && (
							<EditorClassificationTagWrapper
								{...classificationProps}
								shouldRenderPlaceholder={shouldRenderClassificationPlaceholder}
							/>
						)}
					</Box>
					{fg('confluence_modernized_breadcrumbs') && (
						<EditorClassificationTagWrapper
							{...classificationProps}
							shouldRenderPlaceholder={shouldRenderClassificationPlaceholder}
						/>
					)}
					<Box
						xcss={[
							componentsWrapperStyles,
							fg('confluence_modernized_breadcrumbs') && noWrapComponentsWrapperStyles,
						]}
					>
						{!hideSaveIndicator && (
							<SaveIndicator
								editorActions={editorActions}
								saveError={!!collabEditState.collabEditError || userOffline}
								title={title}
							/>
						)}
						{fg('confluence_team_presence_general_availability') ? (
							<ContentHeaderPresenceLoader
								contentId={contentId}
								contentType={contentType}
								analyticSource="editPageScreen"
							/>
						) : (
							<EditorPresenceAvatarGroupLoader
								isLivePage={isLivePage}
								collabEditOption={collabEditOption}
							/>
						)}
						<VerticalSeparator />
						{!hasEditPermission && (
							<ButtonWrapper>
								<RequestToEditLoaderAfterPaint
									contentId={contentId}
									requestToEditContent={requestToEditContent}
									hasRequestedEditPermission={hasRequestedEditPermission}
								/>
							</ButtonWrapper>
						)}
						<EditorPrimaryToolbarComponents
							forceDisabled={forceDisabled}
							userOffline={userOffline}
							contentId={contentId}
							contentType={contentType}
							isContentCreationPanelOpen={isContentCreationPanelOpen}
							isUnpublishedDraft={isUnpublishedDraft}
							onClickPublish={onClickPublish}
							onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
							onPreviewClicked={onPreviewClicked}
							saveDraft={saveDraft}
							spaceKey={spaceKey}
							title={title}
							collabLimitReached={collabLimitReached}
							showRevertDialog={showRevertDialog}
							retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
							isPublishingContent={isPublishingContent}
							editorActions={editorActions}
							isSingleUserSession={isSingleUserSession}
							navigateOnClose={navigateOnClose}
							triggerAnalyticsEvent={triggerAnalyticsEvent}
							shouldRenderPreview={shouldRenderPreview}
							shouldShowRecentSpotlight={shouldShowRecentSpotlight}
							checkIfConditionPreventingPublish={checkIfConditionPreventingPublish}
							flags={flags}
							viewMode={viewMode}
							hasEditPermission={hasEditPermission}
							ellipsisEditorActions={ellipsisEditorActions}
						/>
					</Box>
				</ClassificationModalContextProvider>
			</LiveDocSpotlightsTourManager>
		</Box>
	);
};

// Returns a stable version of the given function.
// The value (function reference) returned on the first call would always be the same,
// but the called function would always be the latest version passed as the argument.
// In a sense, it's a version of useCallback, which allows to not care about the dependencies.
export function useStableFunc<T extends (...args: any[]) => any>(func: T): T {
	const ref = useRef<T>();
	ref.current = func;
	const stableFunc = useCallback((...args: any) => ref.current?.(...args), []);
	return stableFunc as unknown as T;
}

const MemoEditorActionBar = memo(EditorActionBar);

const MemoizedEditorActionBar = (props: EditorActionBarProps) => {
	const showRevertDialog = useStableFunc(props.showRevertDialog);
	const onClickPublish = useStableFunc(props.onClickPublish);
	return (
		<MemoEditorActionBar
			{...props}
			showRevertDialog={showRevertDialog}
			onClickPublish={onClickPublish}
		/>
	);
};

export { MemoizedEditorActionBar as EditorActionBar };
